import { RegionType } from '@protector/entities/region/constants';
import type { UUID } from '@protector/utils/types';
import type { RiskDTO, pageRiskDTO } from 'pages/borer-risk/borer-risk-map/borer-risk.models';
import type { Dictionary } from 'redux-ngrx-entity';
import type { BorerRiskRegion, LimitValue } from '@protector/entities/borer-risk/models';

const isDirectParent = (regionId: UUID, regions: Dictionary<BorerRiskRegion>): boolean => {
  return regions[regionId].children.reduce<boolean>((acc, childId) => {
    if (regions[childId]?.type === RegionType.AREA) return acc;
    return false;
  }, true);
};

const getRiskParameterAverage = (actualAreaRisks: RiskDTO, regions: UUID[], key: string) => {
  let validChildCount = regions.length;

  const value = regions.reduce<number>((sum, childId) => {
    if (!actualAreaRisks[childId] || typeof actualAreaRisks[childId] === 'string') {
      validChildCount--;
      return sum;
    }

    const childRisk = actualAreaRisks[childId];
    if (typeof childRisk !== 'string' && childRisk?.[key] && typeof childRisk[key] === 'number') {
      return sum + childRisk[key];
    }
    validChildCount--;
    return sum;
  }, 0);

  return value ? Number.parseFloat((value / validChildCount).toFixed(2)) : null;
};

const getRegionRisks = (region: BorerRiskRegion, parameterKeys: string[], areaRisks: RiskDTO): Dictionary<number | null> => {
  const result: Dictionary<number | null> = {};

  parameterKeys.forEach(key => {
    result[key] = getRiskParameterAverage(areaRisks, region.children, key);
  });
  return result;
};

export const getRiskDTOFromResponse = (data: RiskDTO | pageRiskDTO): RiskDTO => {
  return data?.content ? (data.content as RiskDTO) : (data as RiskDTO);
};

export const getRisksPerRegion = (regions: Dictionary<BorerRiskRegion>, areaRisks: RiskDTO): RiskDTO => {
  const totalRiskData: RiskDTO = {};
  const parameterKeys = Object.keys(Object.values(areaRisks).find(r => typeof r !== 'string') ?? Object.values(areaRisks)[0]);

  Object.keys(regions).forEach(key => {
    // If the region is an area, simply grab the data returned from the request
    if (regions[key]?.type === RegionType.AREA && getRiskDTOFromResponse(areaRisks)[key]) {
      totalRiskData[key] = getRiskDTOFromResponse(areaRisks)[key];
      return;
    }

    // If the region is a direct parent of one of the areas, calculate the risk
    if (regions[key]?.type === RegionType.REGION && isDirectParent(key, regions)) {
      totalRiskData[key] = getRegionRisks(regions[key], parameterKeys, areaRisks);
    }
  });
  return totalRiskData;
};

export const getParameterLimits = (allRisks: RiskDTO, keys: string[]) => {
  const limitValues = new Map<string, LimitValue>();

  keys.forEach(key => {
    const values = allRisks[key];
    if (typeof values === 'string') return;

    Object.keys(values).forEach(parameterKey => {
      const value = values[parameterKey];
      if (!value) return;
      if (!limitValues.has(parameterKey)) {
        limitValues.set(parameterKey, {
          min: value,
          max: value
        });
      } else {
        const currentLimitValue = limitValues.get(parameterKey);

        if (currentLimitValue) {
          limitValues.set(parameterKey, {
            min: value < currentLimitValue.min ? value : currentLimitValue.min,
            max: value > currentLimitValue.max ? value : currentLimitValue.max
          });
        }
      }
    });
  });

  return Object.fromEntries(limitValues);
};
